import { useState } from 'react';
import { Trans } from '@lingui/macro';
import styled from '@emotion/styled';
import { Text } from 'components/text/Text';
import { DatePicker, DatePickerProps } from 'components';
import { formatDate } from './transforms';

export const placeholder = 'DD-MMM-YYYY';

export type DateProps = {
  from?: Date | null;
  to?: Date | null;
  onChange: (from?: Date | null, to?: Date | null, doUpdate?: boolean) => void;
  sx?: DatePickerProps['sx'];
  inputVariant?: DatePickerProps['inputVariant'];
  isStopPropagationOnKeyDown?: boolean;
  'data-testid'?: string;
};

export const DateFilter = ({
  from,
  to,
  onChange,
  'data-testid': dataTestId,
  ...rest
}: DateProps) => {
  const [rangeError, setRangeError] = useState(false);
  return (
    <Container>
      <FromTo>
        <DatePicker
          {...rest}
          id="filter-from"
          placeholder={placeholder}
          initialDate={formatDate(from)}
          onBlur={(date, stringDate, isValidDate) => {
            if (!isValidDate && stringDate) return;
            const fromDate = date?.toDate();
            if (fromDate && to && fromDate > to) {
              setRangeError(true);
              onChange(fromDate, to, false);
              return;
            }
            setRangeError(false);
            onChange(fromDate, to, true);
          }}
          data-testid={`${dataTestId}_from`}
        />
        <span data-testid={`${dataTestId}_divider_to`}>
          <Trans>to</Trans>
        </span>
        <DatePicker
          {...rest}
          id="filter-to"
          placeholder={placeholder}
          initialDate={formatDate(to)}
          onBlur={(date, stringDate, isValidDate) => {
            if (!isValidDate && stringDate) return;
            const toDate = date?.toDate();
            if (toDate && from && toDate < from) {
              setRangeError(true);
              onChange(from, toDate, false);
              return;
            }
            setRangeError(false);
            onChange(from, toDate, true);
          }}
          data-testid={`${dataTestId}_to`}
        />
      </FromTo>
      {rangeError && (
        <Text color="error" variant="bodySm">
          <Trans>Start date must be before end date</Trans>
        </Text>
      )}
    </Container>
  );
};

const FromTo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  > div {
    min-width: 186px;
  }
  > span {
    height: 56px;
    display: flex;
    align-items: center;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  > span {
    margin-left: ${p => p.theme.s.xxxs};
    margin-right: ${p => p.theme.s.xxxs};
  }
`;
